import React, { useState, useEffect } from 'react';
import { Menu, Sun, Moon, Volume2, VolumeX, Maximize, Minimize, Settings, Sparkles } from 'lucide-react';
import { Wheel } from '../../types';

interface AppHeaderProps {
  currentWheel?: Wheel;
  onToggleSidebar: () => void;
  soundEnabled: boolean;
  onToggleSound: () => void;
  isDark: boolean;
  onToggleTheme: () => void;
  onOpenSettings: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  currentWheel,
  onToggleSidebar,
  soundEnabled,
  onToggleSound,
  isDark,
  onToggleTheme,
  onOpenSettings,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  return (
    <header
      className="glass-panel"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.5rem clamp(0.5rem, 2vw, 1.25rem)',
        borderRadius: '16px',
        marginBottom: '0.75rem',
        width: '100%',
        minWidth: 0,
      }}
    >
      {/* Left: Sidebar Mobile Trigger & Wheel Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
        <button
          onClick={onToggleSidebar}
          className="btn-icon"
          title="Toggle Wheel List Sidebar"
          aria-label="Toggle Wheel List Sidebar"
          style={{ width: '34px', height: '34px', flexShrink: 0 }}
        >
          <Menu size={17} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <h2
            style={{
              fontSize: 'clamp(0.95rem, 3.5vw, 1.15rem)',
              fontWeight: 700,
              fontFamily: 'var(--font-display)',
              color: 'var(--text-primary)',
              maxWidth: 'clamp(110px, 32vw, 280px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentWheel?.name || 'ChittiWin'}
          </h2>
          {currentWheel?.description && (
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                display: 'none', // Shown on large desktop screens
              }}
              className="desktop-desc"
            >
              • {currentWheel.description}
            </span>
          )}
        </div>
      </div>

      {/* Right: Sound, Theme, Fullscreen, Settings */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
        <button
          onClick={onToggleSound}
          className="btn-icon"
          title={soundEnabled ? 'Sound Enabled' : 'Sound Muted'}
          aria-label={soundEnabled ? 'Sound Enabled' : 'Sound Muted'}
          style={{ width: '34px', height: '34px' }}
        >
          {soundEnabled ? <Volume2 size={16} color="var(--accent-primary)" /> : <VolumeX size={16} />}
        </button>

        <button
          onClick={onToggleTheme}
          className="btn-icon"
          title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          style={{ width: '34px', height: '34px' }}
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        <button
          onClick={toggleFullscreen}
          className="btn-icon"
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          style={{ width: '34px', height: '34px' }}
        >
          {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        </button>

        <button
          onClick={onOpenSettings}
          className="btn-icon"
          title="Open Settings"
          aria-label="Open Settings"
          style={{ width: '34px', height: '34px' }}
        >
          <Settings size={16} />
        </button>
      </div>
    </header>
  );
};
